import { ActivatedRoute, Router } from '@angular/router';
import { Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import {
  NodeParameterType,
  NodeParameterTypeRequestModel,
  NodeRecordsRequestModel,
  NodeSensorRecordsRequestModel,
} from 'src/app/models/nodeRecords.model';
import { NodeRecord } from 'src/app/models/sensorRecord.model';
import { Observable, Subscription, forkJoin, of, take, finalize, timeout, TimeoutError, EMPTY, catchError, filter, map, switchMap } from 'rxjs';

import { AuthService } from 'src/app/services/auth.service';
import { ChartService } from 'src/app/services/chart.service';
import { CommonService } from 'src/app/services/common.service';
import { CustomEntityService } from 'src/app/services/customEntity.service';
import { CustomObjectLookup } from 'src/app/models/customObjectLookup.model';
import { EndpointLookup } from 'src/app/models/endpointLookup.model';
import { DateFilterTypeEnum } from '../../enums/dateFilterType.enum';
import { DateFormatService } from '../../services/dateFormat.service';
import { LoaderService } from '../../services/loader.service';
import { NodeRecordChartModel } from 'src/app/models/sensorRecordChart.model';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { ParameterFieldEnum, ParameterTypeEnum } from 'src/app/enums/parameterType.enum';
import { Parameter, ParameterLookup, ParameterThreshold } from '../../models/parameter.model';
import { BatteryStatus, RecordDto } from 'src/app/models/recordDto.model';
import { SensorAssignmentService } from '../../services/sensorAssignment.service';
import { SensorRecordService } from 'src/app/services/sensorRecord.service';
import { ToastService } from '../../services/toast.service';
import { UserPreferences } from 'src/app/models/userPreferences.model';
import { ValueConversionService } from 'src/app/services/value-conversion.service';
import { VideoResource } from 'src/app/models/videoResource.model';
import { NotificationService } from 'src/app/services/notification.service';
import { AppNotification } from 'src/app/models/notification';
import { FilteredNotificationPayload } from 'src/app/models/notification';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CommentsDialogComponent } from 'src/app/common-components/comments-dialog/comments-dialog.component';
import { Comment } from 'src/app/models/comment.model';
import { CommentService } from 'src/app/services/comment.service';
import { AlertEventState, AlertSeverityLevel, UNKNOWN_ALERT_SEVERITY, alertSeverityLabel } from 'src/app/enums/alert.enum';
import { ParameterService } from 'src/app/services/parameter.service';
import { normalizeThresholdColorHex } from 'src/app/shared/utils/threshold-color.util';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { GridPermissions } from 'src/app/shared/grid/core-grid/gridPermissions.enum';
import {
  AlertPeriodOverlay,
  ChartPlotBounds,
  ChartDataPoint,
  ChartSeriesInput,
  NoteRangeOverlay,
  NodeAnalyticsChartComponent,
  NotificationPoint,
  ThresholdOverlayLine,
  YAxisControl
} from './node-analytics-chart/node-analytics-chart.component';

import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
import { toTimestampMs } from 'src/app/shared/utils/timestamp.util';
import { getDashboardNodeIds } from 'src/app/shared/utils/dashboard-nodes.util';
import { DateTimeRange } from 'src/app/models/dateRange.model';

export interface NodeDetails {
  endpointName: string;
  humidity: string;
  pressure: number;
  temperature: string;
  analyteName: string;
  analyteValue: string;
  windSpeed: number | string;
  windSpeedUnit: string;
  windDirection: number;
  batteryPercentage: string;
  deviceId: string;
  deviceName: string;
  statusId: number | string;
  lastOnline: string;
  lastReading: string;
  signalStrength: string;
}

export interface ChartSeriesWithAxis extends ChartSeriesInput {
  axisAssignment: 'L' | 'R' | '3';
  color: string;
  seriesIndex: number;
  parameterIndex: number;
  hidden?: boolean;
}

interface SelectedEndpoint extends CustomObjectLookup {
  color?: string;
}

interface NodeDetailDateRange {
  id?: number;
  start?: number;
  end?: number;
}

type NodeRecordWithFields = NodeRecord & Record<string, string | number | null | undefined>;

@Component({
  standalone: false,
  selector: 'app-node-detail',
  templateUrl: './node-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./node-detail.component.scss']
})
export class NodeDetailComponent implements OnInit, OnDestroy {
  @ViewChild(NodeAnalyticsChartComponent) analyticsChartComponent!: NodeAnalyticsChartComponent;
  @ViewChild('chartContainer') chartContainerRef!: ElementRef<HTMLElement>;
  readonly CHART_DIV_ID = 'node-detail-chart';
  private readonly WIND_DIRECTION_OFFSET = 135;
  private readonly SERIES_COLORS = ['#5B6BD5', '#E57C3A', '#44A98C', '#C0443A', '#8B5CF6', '#D4A017', '#2E86AB', '#6B7280'];

  endPointId!: string;
  endPointName!: string;
  preferences!: UserPreferences;
  showCompareDialog = false;
  showParamsDialog = false;
  subscriptions: Subscription[] = [];
  private nodeRecordsRequestSub: Subscription | null = null;
  private commentsRequestSub: Subscription | null = null;
  private lastNodeRecordsRequestKey = '';
  selectedParameter!: ParameterLookup;
  selectedDateRange: NodeDetailDateRange | null = null;
  selectedOrg!: OrganizationLevel;
  endPoints!: EndpointLookup[];
  selectedEndPoints: SelectedEndpoint[] = [];
  sensorRecord!: RecordDto;
  loadedSensorRecords: NodeRecord[] = [];
  endPointsSeries: string[] = [];
  parameters: NodeParameterType[] = [];
  chartHasNoData = false;
  nodeDetails: Partial<NodeDetails> = {};
  columnParameters: (string | number)[] = [];
  videoResources: VideoResource[] = [];
  notificationsList: AppNotification[] = [];
  paramValues: { paramName: string; nodeLabel: string; unit: string; average: number; peak: number; count: number }[] = [];
  columnHeaders: string[] = [];
  unit: string = '';
  comments: Comment[] = [];
  noteRanges: NoteRangeOverlay[] = [];
  isPageInitialized: boolean = false;
  batteryDetails?: BatteryStatus;
  selectedCommentId: string | null = null;
  selectedSectionRange: DateTimeRange | null = null;
  rangePickMode = false;
  rangePickFirstTs: number | null = null;
  private commentDialogRef: MatDialogRef<CommentsDialogComponent> | null = null;
  // Plain field (not a getter) so the template doesn't re-evaluate it every change-detection
  // cycle. Kept in sync wherever commentDialogRef is set/cleared.
  isAddingNote = false;
  windDirectionTransform = 'rotate(0deg)';

  // Am5 chart data
  am5SeriesData: ChartSeriesWithAxis[] = [];
  visibleSeriesData: ChartSeriesWithAxis[] = [];
  notificationPoints: NotificationPoint[] = [];
  alertPeriods: AlertPeriodOverlay[] = [];
  thresholdLines: ThresholdOverlayLine[] = [];
  private parameterThresholds: ParameterThreshold[] = [];
  lastZoom: DateTimeRange | null = null;
  showAxisBanner: boolean = false;
  axisBannerMessage: string = '';
  axisBannerParams: Record<string, string> = {};

  // Command Center Layout Properties
  isPanelCollapsed: boolean = false;
  activeTab: 'summary' | 'compare' | 'notes' = 'summary';
  hasVideoData: boolean = false;
  isMobileSensor: boolean = false;
  currentTimeCursor: number = 0;
  chartPlotBounds: ChartPlotBounds | null = null;
  canPlotParameters: boolean = false;
  isFullscreenMode: boolean = false;
  isMultiAxisEnabled: boolean = false;
  headerReadingsList: { paramName: string; value: string; unit: string; color: string }[] = [];
  hasActiveAlerts = false;
  highestActiveSeverity: AlertSeverityLevel | number | null = null;
  isLayersMenuOpen = false;
  showAlertPeriodsLayer = true;
  showNotificationMarkersLayer = true;
  showThresholdsLayer = true;
  showNotesLayer = false;
  showMinMaxBandLayer = true;
  showMapLayer = false;
  private mapFeedbackPending = false;
  isYAxisMenuOpen = false;
  yAxisControls: YAxisControl[] = [];
  isXAxisMenuOpen = false;
  xRangeStartText = '';
  xRangeEndText = '';
  xRangeFormatHint = '';
  isLoadingNotes = false;
  hasEnabledLayers = true;
  // Guards a stalled node-records request from leaving the loader overlay up forever.
  private readonly NODE_RECORDS_TIMEOUT_MS = 60_000;
  compareNodes: SelectedEndpoint[] = [];
  isNodeSelectOpen = false;
  nodeSearchQuery = '';
  nodeSelectOptions: EndpointLookup[] = [];
  // Null until the header org level and parameter have been queried; the switcher offers nothing before then.
  private dashboardNodeIds: Set<string> | null = null;
  readonly alertSeverityLevel = AlertSeverityLevel;
  readonly commentPermissionObject = RolePermissionObjects.Comment;
  readonly deleteAction = GridPermissions.Delete;
  private queuedChartPlotBounds: ChartPlotBounds | null = null;
  private isChartPlotBoundsUpdateQueued = false;

  constructor(
    private sensorRecordService: SensorRecordService,
    private customEntityService: CustomEntityService,
    private toastService: ToastService,
    private dateFormatService: DateFormatService,
    private chartService: ChartService,
    private valueConversionService: ValueConversionService,
    private parameterService: ParameterService,
    private zone: NgZone,
    private route: ActivatedRoute,
    public loaderService: LoaderService,
    private commonService: CommonService,
    authService: AuthService,
    private sensorAssignmentService: SensorAssignmentService,
    private commentService: CommentService,
    protected notificationService: NotificationService,
    protected dialog: MatDialog,
    private router: Router,
  ) {
    this.preferences = authService.getCurrentUser().preferences;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.unsubscribeCommentEvents();
    if (this.nodeRecordsRequestSub && !this.nodeRecordsRequestSub.closed) {
      this.nodeRecordsRequestSub.unsubscribe();
    }
    if (this.commentsRequestSub && !this.commentsRequestSub.closed) {
      this.commentsRequestSub.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.loaderService.show();
    this.endPointId = this.route.snapshot.params.id;
    this.applyCitedDateRange();
    this.loadHeaderData();
    // switchMap drops a node's parameter-type response that arrives after the user has already moved on
    // (e.g. A -> B -> Back to A), so a stale node's parameters are never applied to the one shown.
    this.subscriptions.push(this.route.paramMap.pipe(
      map((params) => params.get('id')),
      filter((nodeId): nodeId is string => !!nodeId && nodeId !== this.endPointId),
      switchMap((nodeId) => this.switchNode(nodeId)),
    ).subscribe({
      next: (nodeParameterTypes) => {
        this.setInitialParameters(nodeParameterTypes, true);
        this.loadPage(false);
      },
    }));
  }

  toggleNodeSelect(open = !this.isNodeSelectOpen): void {
    this.isNodeSelectOpen = open;
    if (!open) return;
    this.nodeSearchQuery = '';
    if (this.endPoints !== undefined) {
      this.filterNodeSelectOptions();
      return;
    }
    this.customEntityService.getEndPointLookupList().subscribe({
      next: (endPoints) => {
        this.endPoints = endPoints;
        this.filterNodeSelectOptions();
      },
      error: (error) => {
        this.isNodeSelectOpen = false;
        this.toastService.showError(error);
      },
    });
  }

  onNodeSearch(query: string): void {
    this.nodeSearchQuery = query;
    this.filterNodeSelectOptions();
  }

  // The URL carries the node, so Back, refresh and shared links keep working; the query params (cited
  // parameter and date range) go with it.
  selectNode(node: EndpointLookup): void {
    this.isNodeSelectOpen = false;
    if (node.id === this.endPointId) return;
    this.router.navigate(['..', node.id], { relativeTo: this.route, queryParamsHandling: 'preserve' });
  }

  // Only the nodes the Real-Time Dashboard would list for the header org level and parameter, so every
  // option has live data to show; the endpoint lookup supplies their names and types for display and search.
  private filterNodeSelectOptions(): void {
    const query = this.nodeSearchQuery.toLowerCase().trim();
    const endPoints = (this.endPoints ?? []).filter((endPoint) => this.dashboardNodeIds?.has(endPoint.id));
    this.nodeSelectOptions = query
      ? endPoints.filter((endPoint) =>
          endPoint.name.toLowerCase().includes(query) ||
          (endPoint.customObjectName ?? '').toLowerCase().includes(query))
      : endPoints;
  }

  // The route reuses this component when only the node changes. The header org, parameter and date range
  // and the page's own view settings (extra parameters, compare nodes, layers, tab) carry over; only what
  // belongs to the previous node is dropped and reloaded for the new one.
  private switchNode(nodeId: string): Observable<number[]> {
    this.endPointId = nodeId;
    this.nodeDetails = {};
    this.batteryDetails = undefined;
    this.selectedCommentId = null;
    this.selectedSectionRange = null;
    if (!this.isPageInitialized) return EMPTY;
    this.loaderService.show();
    return this.sensorAssignmentService.getNodeParameterTypes(this.nodeParameterTypePayload(nodeId)).pipe(
      catchError((error) => {
        this.toastService.showError(error);
        this.loaderService.hide();
        return EMPTY;
      }),
    );
  }
  private applyCitedDateRange(): void {
    const { from, to } = this.route.snapshot.queryParams;

    const fromMs = from ? Date.parse(from) : NaN;
    const toMs = to ? Date.parse(to) : NaN;
    if (Number.isFinite(fromMs) && Number.isFinite(toMs)) {
      this.commonService.selectedDateFilter({
        id: DateFilterTypeEnum.Custom,
        value: this.commonService.getEnumName(DateFilterTypeEnum, DateFilterTypeEnum.Custom),
        start: Math.floor(fromMs / 1000),
        end: Math.floor(toMs / 1000),
      });
    }
  }

  setSelectedOrg(selectedOrg: OrganizationLevel) {
    if (this.selectedOrg?.id === selectedOrg?.id) {
      return;
    }
    this.selectedOrg = selectedOrg;
    if (this.selectedParameter && this.selectedOrg && this.selectedDateRange) {
      this.loadPage();
    }
  }

  setSelectedParameter(selectedParameter: ParameterLookup) {
    if (
      this.selectedParameter?.id === selectedParameter?.id &&
      this.selectedParameter?.parameterType === selectedParameter?.parameterType
    ) {
      return;
    }
    this.selectedParameter = selectedParameter;
    if (this.selectedOrg && this.selectedParameter && this.selectedDateRange) {
      this.loadPage();
    }
  }

  setSelectedDateRange(selectedDateRange: NodeDetailDateRange) {
    if (
      this.selectedDateRange?.id === selectedDateRange?.id &&
      this.selectedDateRange?.start === selectedDateRange?.start &&
      this.selectedDateRange?.end === selectedDateRange?.end
    ) {
      return;
    }
    this.selectedDateRange = selectedDateRange;
    if (this.selectedOrg && this.selectedParameter && this.selectedDateRange) {
      this.loadPage(this.isPageInitialized ? false : true);
    }
  }

  goBack() {
    window.history.back();
  }

  onChartZoomChange(event: DateTimeRange | null): void {
    this.lastZoom = event;
    if (event) {
      this.computeStats(event.start, event.end);
    } else {
      this.computeStats(null, null);
    }
  }

  onChartRangeSelection(event: DateTimeRange): void {
    if (this.activeTab !== 'notes') return;
    if (!event || event.end <= event.start) return;

    const isCommentDialogOpen = this.dialog.openDialogs
      .some((dialogRef) => dialogRef.componentInstance instanceof CommentsDialogComponent);
    if (isCommentDialogOpen) return;

    this.highlightSection(event.start, event.end);
    this.openAddCommentPopup(event.start, event.end);
  }

  hideDropdown(boolValue?: boolean) {
    this.showCompareDialog = boolValue !== undefined ? boolValue : !this.showCompareDialog;
  }

  hideParamsDropdown(boolValue?: boolean) {
    const nextDialogState = boolValue !== undefined ? boolValue : !this.showParamsDialog;
    this.showParamsDialog = nextDialogState;

    if (nextDialogState && !this.parameters?.length && this.endPointId) {
      this.sensorAssignmentService.getNodeParameterTypes(this.nodeParameterTypePayload(this.endPointId)).subscribe({
        next: (nodeParameterTypes) => {
          this.setInitialParameters(nodeParameterTypes);
        },
        error: (error) => {
          this.toastService.showError(error);
        },
      });
    }
  }

  loadPage(resetSelections = true) {
    this.getNodeDetails();
    this.loadCommentsForPanel();
    forkJoin({
      endPoints: this.endPoints !== undefined ? of(this.endPoints) : this.customEntityService.getEndPointLookupList(),
      nodeParametersType: this.parameters?.length ? of(this.parameters) :
        this.sensorAssignmentService.getNodeParameterTypes(this.nodeParameterTypePayload(this.endPointId)),
    }).subscribe({
      next: (result) => {
        this.endPoints = result.endPoints;
        if (resetSelections) {
          this.setInitialParameters(result.nodeParametersType, false);
          this.selectedEndPoints = [];
          this.syncCompareNodes();
        } else {
          this.setInitialParameters(result.nodeParametersType, true);
          this.updateEndPointSection(this.selectedEndPoints);
        }
        const requests = this.buildNodeRecordRequests();
        this.updateNodeRecords(requests, this.buildNodeRecordsRequestKey(this.endPointsSeries));
        this.isPageInitialized = true;
      },
      error: (error) => {
        this.toastService.showError(error);
        this.loaderService.hide();
      },
    });
  }

  setInitialParameters(nodeParameters: number[] | NodeParameterType[], preserveSelections = false) {
    const selectedParameterType = this.selectedParameter?.parameterType;

    this.parameters = nodeParameters.map((typeOrParameter: number | NodeParameterType) => {
      const type = typeof typeOrParameter === 'number' ? typeOrParameter : typeOrParameter.id;
      const existingParameter = this.parameters.find((parameter) => parameter.id === type);
      const isBaseParameter = type === selectedParameterType;

      return {
        id: type,
        value: this.chartService.getParameterName(type),
        pushed: existingParameter?.pushed ?? isBaseParameter,
        selected: existingParameter?.selected ?? isBaseParameter,
        hidden: isBaseParameter,
      };
    });

    if (!preserveSelections) {
      this.parameters = this.parameters.map((parameter) => ({
        ...parameter,
        pushed: parameter.id === selectedParameterType,
        selected: parameter.id === selectedParameterType,
      }));
    }

    this.updateCanPlotParameters();
  }

  updateEndPointSection(endpoints = this.selectedEndPoints) {
    if (!endpoints || endpoints.length === 0) {
      this.selectedEndPoints = [];
    } else {
      this.selectedEndPoints = endpoints
        .filter((endpoint) => endpoint.id !== this.endPointId && endpoint.selected)
        .map((endpoint) => ({
          ...endpoint,
          selected: true,
          pushed: true,
        }));
      this.applySelectedEndpointColors();
    }

    this.syncCompareNodes();
  }
  
  getNodeDetails(){
    let payload: NodeRecordsRequestModel = {
      orgLevelId: this.selectedOrg?.id,
      analyteId: this.selectedParameter?.id,
    };
    let notificationsPayload: FilteredNotificationPayload = {
      analyteId: this.selectedParameter?.id,
      nodeId: this.endPointId,
      endDate: this.selectedDateRange?.end ?? 0,
      startDate: this.selectedDateRange?.start ?? 0,
      organizationLevelId: this.selectedOrg?.id,
    }
    forkJoin({
      latestNodeRecords: this.sensorRecordService.getLatestNodeRecords(payload),
      batteryStatus: this.batteryDetails ? of(this.batteryDetails) : this.sensorRecordService.getBatteryStatus(this.endPointId),
      notifications: this.notificationService.getNodeAlertEvents(notificationsPayload),
      parameter: this.selectedParameter?.id
        ? this.parameterService.getParameterById(this.selectedParameter.id)
        : of(null),
    }).subscribe({
      next: (res) => {
        this.parameterThresholds = this.getParameterThresholds(res.parameter);
        this.dashboardNodeIds = getDashboardNodeIds(res.latestNodeRecords);
        if (this.isNodeSelectOpen) {
          this.filterNodeSelectOptions();
        }
        const currEndpoint = res.latestNodeRecords.find(value => value.endPointId == this.endPointId);
        const valueString = (val: number | null | undefined, type: ParameterTypeEnum): string => {
          const value = this.valueConversionService.getParameterValueFromUserPreferences(val, type);
          const numericValue = typeof value === 'number' ? value : Number(value);
          const unit = this.valueConversionService.getParameterUnit(type);
          return Number.isFinite(numericValue) ? parseFloat(numericValue.toFixed(4)).toString() + unit : '-';
        }
        this.notificationsList = res.notifications;
        if (currEndpoint) {
          const batteryPercentage =  res.batteryStatus.batteryPercentage ?? currEndpoint.batteryPercentage;
          this.batteryDetails = res.batteryStatus;
          this.nodeDetails = {
            analyteName: currEndpoint.parameterValue?.name,
            analyteValue: valueString(currEndpoint.parameterValue?.value, this.selectedParameter.parameterType),
            endpointName: currEndpoint.endPointName,
            humidity: valueString(currEndpoint.hum, ParameterTypeEnum.Humidity),
            temperature: valueString(currEndpoint.tempC, ParameterTypeEnum.Temperature),
            windDirection: currEndpoint.windDir ?? 0,
            windSpeed: currEndpoint.windSpeed ? parseFloat(this.valueConversionService.getParameterValueFromUserPreferences(currEndpoint.windSpeed, ParameterTypeEnum.WindSpeed).toFixed(2)) : '-',
            windSpeedUnit: this.valueConversionService.getParameterUnit(ParameterTypeEnum.WindSpeed),
            pressure: currEndpoint.pressure ?? '-',
            batteryPercentage: batteryPercentage ? batteryPercentage + '%' : '-',
            deviceId: currEndpoint.deviceId ?? '-',
            deviceName: res.batteryStatus.deviceName ?? '-',
            statusId: currEndpoint.statusId ?? '-',
            lastOnline: currEndpoint.timestamp ? this.dateFormatService.formatTimestampToString(currEndpoint.timestamp) : '-',
            lastReading: this.formatLastReading(currEndpoint.timestamp),
            signalStrength: this.getSignalStrengthString(currEndpoint.signal_strength)
          }
          this.windDirectionTransform = this.getWindDirectionTransform(currEndpoint.windDir);
        } else {
          this.nodeDetails = {
            endpointName: res.batteryStatus.endPointName ?? '-',
            deviceId: res.batteryStatus.deviceId ?? '-',
            deviceName: res.batteryStatus.deviceName ?? '-',
            lastOnline: res.batteryStatus.timestamp ? this.dateFormatService.formatTimestampToString(res.batteryStatus.timestamp) : '-',
            lastReading: this.formatLastReading(res.batteryStatus.timestamp),
          };
          this.windDirectionTransform = this.getWindDirectionTransform(null);
        }
        this.rebuildAlertOverlays();
        this.updateAlertHeaderState();
      },
      error: (error) => {
        this.toastService.showError(error);
      },
    });
  }

  /**
   * Formats the last-reading timestamp in the user's timezone with the timezone
   * abbreviation appended (e.g. "06/14/2026 15:54 PDT"), respecting the user's
   * configured date/time format preference. Returns '-' when no timestamp exists.
   */
  private formatLastReading(timestamp: number | null | undefined): string {
    if (!timestamp) {
      return '-';
    }
    return this.dateFormatService.formatTimestampToString(
      timestamp,
      `${this.dateFormatService.userDateWithTimeFormat} zzz`
    );
  }

  private getWindDirectionTransform(direction: number | null | undefined): string {
    if (direction == null) {
      return 'rotate(0deg)';
    }

    return `rotate(${direction + this.WIND_DIRECTION_OFFSET}deg)`;
  }

  getSignalStrengthString(signalStrength: unknown): string {
    const strength = Number(signalStrength);
    if (isNaN(strength) || strength < 0) {
      return 'Unknown';
    }
    const range = Math.floor(strength / 20);
    switch (range) {
      case 0: return 'Very Weak';
      case 1: return 'Weak';
      case 2: return 'Fair';
      case 3: return 'Good';
      case 4: case 5: return 'Excellent';
      default: return 'Invalid Signal Strength';
    }
  }

  nodeRecordsPayload(endPointId: string) {
    const additionalParameterTypes = this.getActiveParameterIds()
      .filter((parameterId) => parameterId !== this.selectedParameter.parameterType);


    const payload: NodeSensorRecordsRequestModel = {
      orgLevelId: this.selectedOrg?.id,
      analyteId: this.selectedParameter?.id,
      endPointId: endPointId,
      dateFilterType: (this.selectedDateRange?.id || this.selectedDateRange?.id === 0) ?
                        this.selectedDateRange?.id : DateFilterTypeEnum.AllDates,
      startDate: this.selectedDateRange?.start ? this.selectedDateRange?.start : 0,
      endDate: this.selectedDateRange?.end ? this.selectedDateRange?.end : 0,
      includeVideo: true,
      parameterTypes: Array.from(new Set([
        ...additionalParameterTypes,
        // lat/lon exist only to power the opt-in map. They are the largest fields in a
        // record, so on a fixed node (the normal case) they are pulled and discarded.
        // Request them only when the user has turned the map on.
        ...(this.showMapLayer ? [ParameterFieldEnum.lat, ParameterFieldEnum.lon] : []),
      ])),
    };
    return payload;
  }

  nodeParameterTypePayload(nodeId: string) {
    const payload: NodeParameterTypeRequestModel = {
      nodeId: nodeId,
      dateFilterType: (this.selectedDateRange?.id || this.selectedDateRange?.id === 0) ?
                        this.selectedDateRange?.id : DateFilterTypeEnum.AllDates,
      startDate: this.selectedDateRange?.start ? this.selectedDateRange?.start : 0,
      endDate: this.selectedDateRange?.end ? this.selectedDateRange?.end : 0,
    };
    return payload;
  }

  private buildNodeRecordsRequestKey(endpointIds: (string | number)[]): string {
    const normalizedEndpointIds = [...endpointIds].sort((a, b) => String(a).localeCompare(String(b))).join(',');
    const normalizedParameterIds = this.getActiveParameterIds().sort((a, b) => a - b).join(',');
    return [
      this.selectedOrg?.id ?? 0,
      this.selectedParameter?.id ?? 0,
      this.selectedParameter?.parameterType ?? 0,
      this.selectedDateRange?.id ?? -1,
      this.selectedDateRange?.start ?? 0,
      this.selectedDateRange?.end ?? 0,
      normalizedParameterIds,
      normalizedEndpointIds,
      // Part of the key so turning the map on/off is a distinct request — otherwise the
      // duplicate-request guard in updateNodeRecords() blocks the coordinate re-fetch.
      this.showMapLayer ? 1 : 0,
    ].join('|');
  }

  updateNodeRecords(requests: Observable<NodeRecordChartModel>[], requestKey = '') {
    if (!requests || requests.length === 0) {
      this.chartHasNoData = true;
      this.loaderService.hide();
      return;
    }

    if (
      requestKey &&
      this.lastNodeRecordsRequestKey === requestKey &&
      this.nodeRecordsRequestSub &&
      !this.nodeRecordsRequestSub.closed
    ) {
      return;
    }

    if (this.nodeRecordsRequestSub && !this.nodeRecordsRequestSub.closed) {
      this.nodeRecordsRequestSub.unsubscribe();
    }

    this.lastNodeRecordsRequestKey = requestKey;
    this.loaderService.show();
    this.updateTableParams();
    this.nodeRecordsRequestSub = forkJoin(requests)
      .pipe(
        timeout({ each: this.NODE_RECORDS_TIMEOUT_MS }),
        // finalize runs on success, on error/timeout, AND on unsubscribe when a newer request
        // supersedes this one — the next/error handlers cannot cover unsubscribe. Clearing
        // mapFeedbackPending here (not only in buildAndRenderSeries) guarantees the flag never
        // outlives the request that set it, so the "no location data" warning can't surface on a
        // later, unrelated render after this fetch errors, times out, or is superseded.
        finalize(() => {
          this.loaderService.hide();
          this.mapFeedbackPending = false;
        }),
      )
      .subscribe({
        next: (results: NodeRecordChartModel[]) => {
          if (results) {
            this.buildAndRenderSeries(results);
          }
        },
        error: (error) => {
          if (error instanceof TimeoutError) {
            this.toastService.displayErrorMessage('The request timed out. Please try again.', true);
          } else {
            this.toastService.showError(error);
          }
        }
      });
  }

  updateTableParams() {
    this.paramValues = [];
    const activeEndpoints = this.getActiveEndpoints();
    const activeParameters = this.getActiveParameters();

    if (activeEndpoints.length > 1) {
      this.columnParameters = activeEndpoints.map((endpoint) => endpoint.id);
      this.columnHeaders = activeEndpoints.map((endpoint) => endpoint.name);
    } else {
      this.columnParameters = activeParameters.map((parameter) => parameter.id);
      this.columnHeaders = activeParameters.map((parameter) =>
        parameter.id === this.selectedParameter.parameterType
          ? this.selectedParameter.name
          : this.chartService.getParameterName(parameter.id));
    }
    this.unit = this.valueConversionService.getParameterUnit(this.selectedParameter.parameterType) || '';
  }

  plotRecords() {
    this.loaderService.show();
    this.parameters = this.parameters.map((parameter) => ({
      ...parameter,
      pushed: parameter.selected,
    }));
    this.updateEndPointSection(this.selectedEndPoints);
    this.updateNodeRecords(this.buildNodeRecordRequests(), this.buildNodeRecordsRequestKey(this.endPointsSeries));
    this.showCompareDialog = false;
  }

  updateEndPoint(item: SelectedEndpoint, event: Event) {
    item.selected = (event.target as HTMLInputElement)?.checked;
  }

  cancelPlottedRecords() {
    this.showCompareDialog = false;
    this.selectedEndPoints.forEach((ep, ei) => {
      this.selectedEndPoints[ei]['selected'] = ep.pushed;
    });
  }

  updateParameterSelection(i: number, event: Event) {
    this.parameters[i] = {...this.parameters[i], selected: (event.target as HTMLInputElement)?.checked};
    this.updateCanPlotParameters();
  }

  plotParametersRecords() {
    if (!this.canPlotParameters) {
      return;
    }

    this.loaderService.show();
    this.parameters = this.parameters.map((parameter) => ({
      ...parameter,
      pushed: parameter.selected,
    }));
    this.updateNodeRecords(this.buildNodeRecordRequests(), this.buildNodeRecordsRequestKey(this.endPointsSeries));
    this.showParamsDialog = false;
  }

  cancelParametersRecords() {
    this.showParamsDialog = false;
    this.parameters = this.parameters.map((parameter) => ({
      ...parameter,
      selected: parameter.pushed,
    }));
    this.updateCanPlotParameters();
  }

  private updateCanPlotParameters(): void {
    this.canPlotParameters = this.parameters.some((param) => !param.hidden && param.selected !== param.pushed);
  }

  buildAndRenderSeries(sensorRecordsArray: NodeRecordChartModel[]): void {
    this.loadedSensorRecords = [];
    this.am5SeriesData = [];
    this.videoResources = [];
    this.showAxisBanner = false;

    const selectedParams = this.getActiveParameterIds();
    const nodeColorMap = this.getNodeColorMap();

    const preferences = this.valueConversionService.preferences;
    const selectedParamType = this.selectedParameter?.parameterType;
    const selectedParamName = this.selectedParameter
      ? (this.chartService.getParameterName(selectedParamType) || this.selectedParameter.name)
      : '';
    const selectedParamField = this.selectedParameter
      ? ParameterFieldEnum[selectedParamType]
      : '';

    if (selectedParams.length > 1) {
      this.isMultiAxisEnabled = true;
    }

    this.endPointsSeries = this.endPointsSeries.filter(
      (_, ei) => sensorRecordsArray[ei]?.nodeRecords?.length > 0
    );
    this.videoResources = [];
    sensorRecordsArray
      .filter(sr => sr?.nodeRecords?.length > 0)
      .forEach((sr, si) => {
        const sensorRecords = sr.nodeRecords;
        sr.videoResources.forEach(r => {
          r.timestampStart = this.dateFormatService.normalizeTimestamp(r.timestampStart);
          r.timestampEnd = this.dateFormatService.normalizeTimestamp(r.timestampEnd);
        });
        this.videoResources = [...this.videoResources, ...sr.videoResources];

        const seriesEndPointId = this.endPointsSeries[si];
        const seriesEndPointName = this.endPoints.find(e => e.id === seriesEndPointId)?.name;
        sensorRecords.forEach((record) => {
          const mutableRecord = record as NodeRecordWithFields;
          record.endPointId = seriesEndPointId;
          record.timestamp *= 1000;
          if (record?.value != null && this.selectedParameter) {
            mutableRecord[selectedParamName] = this.valueConversionService.getParameterValueFromUserPreferences(
              record.value, selectedParamType, preferences);
            mutableRecord[selectedParamField] = mutableRecord[selectedParamName] as number;
          }
          if (record.values) {
            for (const [key, value] of Object.entries(record.values)) {
              const parameterType = Number(key) as ParameterTypeEnum;
              const fieldName = ParameterFieldEnum[parameterType];
              mutableRecord[fieldName] = this.valueConversionService.getParameterValueFromUserPreferences(value, parameterType, preferences);
            }
          }
          mutableRecord['deviceName'] = this.nodeDetails?.deviceName;
          mutableRecord['deviceId'] = this.nodeDetails?.deviceId;
          mutableRecord['statusId'] = this.nodeDetails?.statusId;
          mutableRecord['endPointName'] = seriesEndPointName;
        });
        for (const loadedRecord of sensorRecords) {
          this.loadedSensorRecords.push(loadedRecord);
        }

        const nodeId = seriesEndPointId;
        const nodeName = seriesEndPointName || '';

        selectedParams.forEach((paramId, parameterIndex) => {
          const paramName = this.resolveSeriesParameterName(paramId);
          const paramUnit = this.valueConversionService.getParameterUnit(paramId) || '';
          const data: ChartDataPoint[] = sensorRecords.map((record) => {
            const mutableRecord = record as NodeRecordWithFields;
            const fieldKey = ParameterFieldEnum[paramId];
            const val = mutableRecord[fieldKey];
            return { timestamp: record.timestamp, value: typeof val === 'number' ? val : null };
          });
          const seriesIndex = this.am5SeriesData.length;
          const color = nodeColorMap.get(nodeId) ?? this.getSeriesColor(si);
          const series: ChartSeriesWithAxis = {
            data,
            paramId,
            paramName,
            paramUnit,
            nodeId,
            nodeName,
            nodeIndex: si,
            axisAssignment: 'L',
            color,
            seriesIndex,
            parameterIndex,
            axisOverride: 0,
            hidden: false
          };
          this.am5SeriesData.push(series);
        });
      });

    this.rebuildAlertOverlays();

    this.chartHasNoData = this.am5SeriesData.length === 0;
    this.assignAxisToSeries();
    this.syncVisibleSeriesData();
    this.computeStats(null, null);
    // Mobile-sensor detection is meaningful only when coordinates were actually requested.
    // With the map off there is no lat/lon in the records, so keep isMobileSensor false and
    // the sync panel behaves as it does today for a fixed node.
    if (this.showMapLayer) {
      this.detectMobileSensor(this.loadedSensorRecords);
      if (this.mapFeedbackPending) {
        this.mapFeedbackPending = false;
        if (!this.isMobileSensor) {
          this.toastService.showWarning('This node has no location data to show on the map.', true);
        }
      }
    } else {
      this.isMobileSensor = false;
    }
    this.updateVideoAndMapState();
    // If the Y-Range menu is open when data reloads (date change, series toggle), the prefilled
    // min/max would otherwise reflect the previous dataset and Apply would pin to stale numbers.
    // Recompute the controls against the freshly rendered axes so the inputs stay in sync.
    if (this.isYAxisMenuOpen) {
      this.refreshYAxisControls();
    }
  }

  computeStats(start: number | null, end: number | null): void {
    this.paramValues = this.visibleSeriesData.map((series) => {
      let total = 0;
      let peak = Number.NEGATIVE_INFINITY;
      let count = 0;

      series.data.forEach((point) => {
        if (point.value == null) {
          return;
        }

        if (start != null && end != null && (point.timestamp < start || point.timestamp > end)) {
          return;
        }

        total += point.value;
        peak = Math.max(peak, point.value);
        count += 1;
      });

      return {
        paramName: series.paramName,
        nodeLabel: series.nodeName || this.endPointName || this.nodeDetails?.endpointName || '',
        unit: series.paramUnit,
        average: count > 0 ? total / count : 0,
        peak: count > 0 ? peak : 0,
        count,
      };
    });
  }

  openAddCommentPopup(startDate: number, endDate: number) {
    const start = Math.min(startDate, endDate);
    const end = Math.max(startDate, endDate);
    this.highlightSection(start, end);
    const data = {
      fromDate: Math.floor(start / 1000),
      toDate: Math.floor(end / 1000),
      endPointId: this.endPointId
    }
    const dialogRef = this.dialog.open(CommentsDialogComponent, {
      data: {
        ...data,
        isReadonly: false
      },
      width: '460px',
      disableClose: true,
      hasBackdrop: false,
      panelClass: 'comment-dialog-container'
    });
    this.commentDialogRef = dialogRef;
    this.isAddingNote = true;

    dialogRef.afterClosed()
    .pipe(take(1))
    .subscribe((result: unknown) => {
      this.commentDialogRef = null;
      this.isAddingNote = false;
      this.rangePickMode = false;
      this.rangePickFirstTs = null;
      this.resetSelectedRange();
      if (result) {
        this.loadCommentsForPanel();
      }
    })
  }

  resetCommentEvents(): void {
    this.unsubscribeCommentEvents();
    this.selectedCommentId = null;
    this.resetSelectedRange();
    this.noteRanges = [];
  }

  enableCommentCreation(): void {
    // Toggle off: if a note is already being created, cancel it (and close the dialog).
    if (this.isAddingNote) {
      this.cancelRangePick();
      return;
    }

    this.selectedCommentId = null;
    this.resetSelectedRange();
    this.rangePickFirstTs = null;
    this.switchTab('notes');
    if (this.isPanelCollapsed) {
      this.togglePanel();
    }

    // Open the add-note dialog immediately so the start/stop times can be entered
    // manually. When the chart has plottable data, also enable click-to-select on the
    // graph (dialog opens with hasBackdrop:false, so the graph stays clickable) — the two
    // picked points populate the dialog's From/To fields.
    this.rangePickMode = !this.chartHasNoData && this.visibleSeriesData.length > 0;

    const { start, end } = this.getCommentRangeBounds();
    this.openAddCommentPopup(start, end);
  }

  onChartRangePickProgress(timestamp: number | null): void {
    this.rangePickFirstTs = timestamp;
  }

  onChartRangePicked(range: DateTimeRange): void {
    this.rangePickFirstTs = null;
    if (!range || range.end <= range.start) {
      return;
    }

    this.highlightSection(range.start, range.end);

    const dialogInstance = this.commentDialogRef?.componentInstance;
    if (dialogInstance) {
      // Dialog is already open — push the picked window into its From/To fields and keep
      // rangePickMode active so the user can re-select a different window if needed.
      dialogInstance.setDateRange(range.start, range.end);
    } else {
      // Fallback (e.g. dialog was dismissed): open a pre-populated dialog.
      this.openAddCommentPopup(range.start, range.end);
    }
  }

  cancelRangePick(): void {
    this.rangePickMode = false;
    this.rangePickFirstTs = null;
    if (this.commentDialogRef) {
      this.commentDialogRef.close();
    }
  }

  showCommentLayers(): void {
    this.resetCommentEvents();
    this.switchTab('notes');
    if (this.isPanelCollapsed) {
      this.togglePanel();
    }
  }

  resetSelectedRange(): void {
    this.selectedSectionRange = null;
  }

  unsubscribeCommentEvents(): void {
    // no-op: comment events are no longer consumed from flyout
  }

  exportData(): void {
    const seriesToExport = this.visibleSeriesData;
    if (!seriesToExport.length) {
      return;
    }

    const formatValue = (val: string | number): string => {
      if (typeof val === 'number') return val.toFixed(4).replace(/\.?0+$/, '');
      return val ?? '';
    };

    const seriesHeaders = seriesToExport.map((series) => {
      const nodeLabel = series.nodeName || this.endPointName || this.nodeDetails?.endpointName || '';
      const unitLabel = series.paramUnit ? ` (${series.paramUnit})` : '';
      return `${nodeLabel ? `${nodeLabel} - ` : ''}${series.paramName}${unitLabel}`;
    });

    const rows = new Map<number, string[]>();
    seriesToExport.forEach((series, seriesIndex) => {
      series.data.forEach((dataPoint) => {
        if (!dataPoint.timestamp) {
          return;
        }

        if (!rows.has(dataPoint.timestamp)) {
          rows.set(dataPoint.timestamp, new Array(seriesToExport.length).fill(''));
        }

        rows.get(dataPoint.timestamp)![seriesIndex] = dataPoint.value == null ? '' : formatValue(dataPoint.value);
      });
    });

    const timestamps = Array.from(rows.keys()).sort((left, right) => left - right);
    const csvLines = [
      ['Timestamp', ...seriesHeaders].join(','),
      ...timestamps.map((timestamp) => [
        this.dateFormatService.formatTimestampToString(timestamp),
        ...rows.get(timestamp)!,
      ].join(',')),
    ];

    const blob = new Blob(['\uFEFF' + csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'data.csv';
    link.click();
  }

  loadHeaderData() {

    this.subscriptions.push(this.commonService.selectedParameterAfterCitation(
      this.route.snapshot.queryParams.parameters,
      (error) => this.toastService.showError(error)
    ).subscribe({
      next: (parameter) => {
        if (parameter) {
          this.setSelectedParameter(parameter);
        }
      }
    }));
    this.subscriptions.push(this.commonService.selectedOrgEmitter.subscribe((selectedOrg) => {
      if (selectedOrg) {
        this.setSelectedOrg(selectedOrg);
      }

    }));
    this.subscriptions.push(this.commonService.selectedDateFilterEmitter.subscribe((dateFilter) => {
      if (dateFilter) {
        this.setSelectedDateRange(dateFilter);
      }

    }));
  }

  togglePanel(): void {
    this.isPanelCollapsed = !this.isPanelCollapsed;
  }

  toggleLayersMenu(): void {
    this.isLayersMenuOpen = !this.isLayersMenuOpen;
  }

  closeLayersMenu(): void {
    this.isLayersMenuOpen = false;
  }

  updateLayerToggle(layer: 'alertPeriods' | 'notifications' | 'thresholds' | 'notes' | 'minMaxBand' | 'map', event: Event): void {
    event.stopPropagation();
    const checked = (event.target as HTMLInputElement)?.checked;
    if (layer === 'alertPeriods') {
      this.showAlertPeriodsLayer = checked;
    } else if (layer === 'notifications') {
      this.showNotificationMarkersLayer = checked;
    } else if (layer === 'thresholds') {
      this.showThresholdsLayer = checked;
    } else if (layer === 'minMaxBand') {
      this.showMinMaxBandLayer = checked;
    } else if (layer === 'map') {
      this.showMapLayer = checked;
      // Enabling fetches records with coordinates and reveals the map; the request key now
      // carries the map flag so this re-fetch is not blocked. Disabling just hides the map
      // (isMobileSensor is gated by showMapLayer in the template) — no extra round trip.
      if (checked) {
        this.mapFeedbackPending = true;
        this.updateNodeRecords(this.buildNodeRecordRequests(), this.buildNodeRecordsRequestKey(this.endPointsSeries));
      }
    } else {
      this.showNotesLayer = checked;
      if (this.showNotesLayer) {
        this.showCommentLayers();
      } else {
        this.resetCommentEvents();
      }
    }

    this.updateLayerEnabledState();
  }

  switchTab(tab: 'summary' | 'compare' | 'notes'): void {
    this.activeTab = tab;
    if (tab === 'notes') {
      this.loadCommentsForPanel();
    }
  }

  private loadCommentsForPanel(): void {
    if (!this.endPointId) {
      return;
    }

    if (this.commentsRequestSub && !this.commentsRequestSub.closed) {
      this.commentsRequestSub.unsubscribe();
    }

    this.isLoadingNotes = true;
    const filterBy = {
      endPointId: this.endPointId,
      dateFilterType: this.selectedDateRange?.id,
      startDate: this.selectedDateRange?.start,
      endDate: this.selectedDateRange?.end,
    };

    this.commentsRequestSub = this.commentService.getComments(filterBy)
      .pipe(take(1))
      .subscribe({
        next: (results) => {
          this.isLoadingNotes = false;
          this.comments = results;
          this.noteRanges = this.comments
            .filter((comment) => Number.isFinite(comment.fromDate) && Number.isFinite(comment.toDate) && comment.toDate > comment.fromDate)
            .map((comment) => ({
              id: comment.id,
              start: comment.fromDate * 1000,
              end: comment.toDate * 1000,
            }));
          if (this.selectedCommentId != null && !this.noteRanges.some((range) => range.id === this.selectedCommentId)) {
            this.selectedCommentId = null;
          }
        },
        error: (error) => {
          this.isLoadingNotes = false;
          this.toastService.showError(error);
        },
      });
  }

  focusCompareTab(): void {
    this.switchTab('compare');
    if (this.isPanelCollapsed) {
      this.togglePanel();
    }
  }

  onCompareSelectionChange(selection: SelectedEndpoint[]): void {
    this.selectedEndPoints = selection.filter((endpoint) => endpoint.id !== this.endPointId);
    this.applySelectedEndpointColors();
    this.syncCompareNodes();
  }

  removeCompareNode(nodeId: string): void {
    this.selectedEndPoints = this.selectedEndPoints.filter(e => e.id !== nodeId);
    this.applySelectedEndpointColors();
    this.syncCompareNodes();
    this.applyCompare();
  }

  applyCompare(): void {
    this.selectedEndPoints = this.selectedEndPoints.map((endpoint) => ({
      ...endpoint,
      selected: true,
      pushed: true,
    }));
    this.applySelectedEndpointColors();
    this.syncCompareNodes();
    this.plotRecords();
    this.switchTab('summary');
  }

  clearCompare(): void {
    this.selectedEndPoints = [];
    this.syncCompareNodes();
    this.plotRecords();
  }

  resetZoom(): void {
    this.lastZoom = null;
    this.computeStats(null, null);
    this.analyticsChartComponent?.zoomFit();
  }

  toggleFullscreen(): void {
    const target = this.chartContainerRef?.nativeElement;
    if (!target) {
      return;
    }

    if (!document.fullscreenElement) {
      target.requestFullscreen().catch(() => {
        // no-op
      });
      return;
    }

    document.exitFullscreen().catch(() => {
      // no-op
    });
  }

  exitFullscreen(): void {
    this.isFullscreenMode = false;
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isFullscreenMode) {
      this.exitFullscreen();
    }
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    this.isFullscreenMode = !!document.fullscreenElement;
  }

  setTimeCursor(timestamp: number): void {
    this.currentTimeCursor = timestamp;
  }

  onChartPlotBoundsChange(bounds: ChartPlotBounds): void {
    this.queuedChartPlotBounds = bounds;

    if (this.isChartPlotBoundsUpdateQueued) {
      return;
    }

    this.isChartPlotBoundsUpdateQueued = true;
    queueMicrotask(() => {
      this.chartPlotBounds = this.queuedChartPlotBounds;
      this.queuedChartPlotBounds = null;
      this.isChartPlotBoundsUpdateQueued = false;
    });
  }

  getSeriesColor(index: number): string {
    return this.SERIES_COLORS[index % this.SERIES_COLORS.length];
  }

  private applySelectedEndpointColors(): void {
    this.selectedEndPoints = this.selectedEndPoints.map((endpoint, index) => ({
      ...endpoint,
      color: this.getSeriesColor(index + 1),
    }));
  }

  private assignAxisToSeries(): void {
    this.applyAxisConfiguration();
  }

  toggleMultiAxis(): void {
    this.isMultiAxisEnabled = !this.isMultiAxisEnabled;
    this.applyAxisConfiguration();

    if (!this.isMultiAxisEnabled) {
      this.toastService.showInfo('All series now on shared left axis — scales may not be comparable', true);
    }
  }

  private applyAxisConfiguration(): void {
    if (!this.am5SeriesData?.length) {
      this.showAxisBanner = false;
      this.axisBannerMessage = '';
      this.axisBannerParams = {};
      this.syncVisibleSeriesData();
      return;
    }

    if (!this.isMultiAxisEnabled) {
      this.am5SeriesData.forEach(series => {
        series.axisAssignment = 'L';
        series.axisOverride = 0;
      });
      this.showAxisBanner = false;
      this.axisBannerMessage = '';
      this.axisBannerParams = {};
      this.syncVisibleSeriesData();
      return;
    }

    const unitGroups = new Map<string, ChartSeriesWithAxis[]>();
    const unitRepresentativeName = new Map<string, string>();
    this.am5SeriesData.forEach(series => {
      const unitKey = series.paramUnit || '-';
      if (!unitGroups.has(unitKey)) {
        unitGroups.set(unitKey, []);
        unitRepresentativeName.set(unitKey, series.paramName);
      }
      unitGroups.get(unitKey)?.push(series);
    });

    let unitIndex = 0;
    const hasMoreThanThreeUnitGroups = unitGroups.size > 3;
    unitGroups.forEach(seriesList => {
      const axis: 'L' | 'R' | '3' = unitIndex === 0 ? 'L' : (unitIndex === 1 ? 'R' : '3');
      seriesList.forEach(series => {
        series.axisAssignment = axis;
        series.axisOverride = this.axisToOverride(axis);
      });
      unitIndex += 1;
    });

    if (hasMoreThanThreeUnitGroups) {
      this.toastService.showInfo('More than three unit groups detected — additional groups are mapped to axis 3', true);
    }

    const uniqueUnits = unitGroups.size;
    if (uniqueUnits === 2) {
      const unitKeys = Array.from(unitGroups.keys());
      const firstName = unitRepresentativeName.get(unitKeys[0]) || 'PM2.5';
      const secondName = unitRepresentativeName.get(unitKeys[1]) || 'Temperature';
      this.showAxisBanner = true;
      this.axisBannerMessage = '{{paramOne}} and {{paramTwo}} use different units';
      this.axisBannerParams = { paramOne: firstName, paramTwo: secondName };
    } else if (uniqueUnits > 2) {
      this.showAxisBanner = true;
      this.axisBannerMessage = '{{paramCount}} parameters use {{unitCount}} different units';
      this.axisBannerParams = {
        paramCount: this.am5SeriesData.length.toString(),
        unitCount: uniqueUnits.toString()
      };
    } else {
      this.showAxisBanner = false;
      this.axisBannerMessage = '';
      this.axisBannerParams = {};
    }

    this.syncVisibleSeriesData();
  }

  private syncVisibleSeriesData(): void {
    this.visibleSeriesData = this.am5SeriesData.filter(series => !series.hidden);
    this.headerReadingsList = this.visibleSeriesData.map((series) => {
      const latest = series.data?.length ? series.data[series.data.length - 1] : null;
      const value = latest?.value != null ? parseFloat(latest.value.toFixed(2)).toString() : '-';
      return { paramName: series.paramName, value, unit: series.paramUnit, color: series.color };
    });
  }

  private axisToOverride(axis: 'L' | 'R' | '3'): 0 | 1 | 2 {
    if (axis === 'R') {
      return 1;
    }
    if (axis === '3') {
      return 2;
    }
    return 0;
  }

  private setAxisForUnit(unit: string, axis: 'L' | 'R' | '3'): void {
    const axisOverride = this.axisToOverride(axis);
    this.am5SeriesData.forEach(series => {
      if (series.paramUnit === unit) {
        series.axisAssignment = axis;
        series.axisOverride = axisOverride;
      }
    });
  }

  deleteComment(commentId: string): void {
    this.loaderService.show();
    this.commentService.delete(commentId).subscribe({
      next: () => {
        this.comments = this.comments.filter(c => c.id !== commentId);
        this.noteRanges = this.noteRanges.filter((range) => range.id !== commentId);
        if (this.selectedCommentId === commentId) {
          this.selectedCommentId = null;
          this.resetSelectedRange();
        }
        this.toastService.showSuccess('Comment deleted successfully', true);
        this.loaderService.hide();
      },
      error: () => {
        this.toastService.displayErrorMessage('Something went wrong', true);
        this.loaderService.hide();
      },
    });
  }

  detectMobileSensor(records: NodeRecord[]): void {
    if (!records || records.length === 0) {
      this.isMobileSensor = false;
      return;
    }

    const uniquePositions = new Set<string>();
    records.forEach(record => {
      const position = this.getRecordPosition(record);
      if (position) {
        uniquePositions.add(`${position.lat},${position.lon}`);
      }
    });

    this.isMobileSensor = uniquePositions.size > 3;
  }

  private getRecordPosition(record: NodeRecord): { lat: number; lon: number } | null {
    const latitudeFromValues = record.values?.[ParameterTypeEnum.Latitude];
    const longitudeFromValues = record.values?.[ParameterTypeEnum.Longitude];
    const dynamicRecord = record as NodeRecord & {
      lat?: number;
      lon?: number;
      latitude?: number;
      Longitude?: number;
      longitude?: number;
      Latitude?: number;
    };
    const latitude = Number(latitudeFromValues ?? dynamicRecord.lat ?? dynamicRecord.latitude ?? dynamicRecord.Latitude);
    const longitude = Number(longitudeFromValues ?? dynamicRecord.lon ?? dynamicRecord.longitude ?? dynamicRecord.Longitude);

    const hasValidCoordinates = Number.isFinite(latitude) &&
      Number.isFinite(longitude) &&
      latitude !== 0 &&
      longitude !== 0 &&
      latitude >= -90 &&
      latitude <= 90 &&
      longitude >= -180 &&
      longitude <= 180;

    return hasValidCoordinates ? { lat: latitude, lon: longitude } : null;
  }

  updateVideoAndMapState(): void {
    this.hasVideoData = this.videoResources && this.videoResources.length > 0;
  }

  public trackById = trackById;
    public trackByIndex = trackByIndex;

  setSeriesAxis(seriesIndex: number, axis: 'L' | 'R' | '3'): void {
    if (this.am5SeriesData[seriesIndex]) {
      this.isMultiAxisEnabled = true;
      const targetSeries = this.am5SeriesData[seriesIndex];
      this.setAxisForUnit(targetSeries.paramUnit, axis);
      // The series are updated in place; a new array is what makes the chart re-place them on their axes.
      this.am5SeriesData = [...this.am5SeriesData];
      this.syncVisibleSeriesData();
      this.computeStats(this.lastZoom?.start ?? null, this.lastZoom?.end ?? null);

      if (axis === '3') {
        this.toastService.showInfo('Series moved to third axis (3)', true);
        this.toastService.showInfo('Axis 3 is the tertiary axis used for a third unit or overflow groups', true);
      }
    }
  }

  toggleSeriesVisibility(seriesIndex: number): void {
    if (this.am5SeriesData[seriesIndex]) {
      this.am5SeriesData[seriesIndex].hidden = !this.am5SeriesData[seriesIndex].hidden;
      // Reassign a new array reference so the chart's @Input() seriesData registers a
      // change and ngOnChanges -> syncSeries() runs, hiding/showing the line on the graph
      // (the element is mutated in place, which alone does not trip Angular change detection).
      this.am5SeriesData = [...this.am5SeriesData];
      this.syncVisibleSeriesData();
      this.computeStats(this.lastZoom?.start ?? null, this.lastZoom?.end ?? null);
    }
  }

  dismissAxisBanner(): void {
    this.showAxisBanner = false;
  }

  private updateLayerEnabledState(): void {
    this.hasEnabledLayers =
      this.showAlertPeriodsLayer ||
      this.showNotificationMarkersLayer ||
      this.showThresholdsLayer ||
      this.showNotesLayer ||
      this.showMinMaxBandLayer ||
      this.showMapLayer;
  }

  toggleYAxisMenu(): void {
    this.isYAxisMenuOpen = !this.isYAxisMenuOpen;
    if (this.isYAxisMenuOpen) {
      this.refreshYAxisControls();
    }
  }

  closeYAxisMenu(): void {
    this.isYAxisMenuOpen = false;
  }

  private refreshYAxisControls(): void {
    this.yAxisControls = this.analyticsChartComponent?.getYAxisControls() ?? [];
  }

  onChartYAxisRangeChange(): void {
    if (this.isYAxisMenuOpen) {
      this.refreshYAxisControls();
    }
  }

  toggleXAxisMenu(): void {
    this.isXAxisMenuOpen = !this.isXAxisMenuOpen;
    if (this.isXAxisMenuOpen) {
      this.refreshXAxisInputs();
    }
  }

  closeXAxisMenu(): void {
    this.isXAxisMenuOpen = false;
  }

  private refreshXAxisInputs(startInput?: HTMLInputElement, endInput?: HTMLInputElement): void {
    const range = this.analyticsChartComponent?.getXRange() ?? null;
    this.xRangeFormatHint = this.dateFormatService.userDateWithTimeFormat;
    this.xRangeStartText = range ? this.dateFormatService.formatTimestampToString(range.start) : '';
    this.xRangeEndText = range ? this.dateFormatService.formatTimestampToString(range.end) : '';
    // [value] is one-way: write the boxes directly so Reset also replaces text the user typed.
    if (startInput) {
      startInput.value = this.xRangeStartText;
    }
    if (endInput) {
      endInput.value = this.xRangeEndText;
    }
  }

  applyXAxisRange(startValue: string, endValue: string): void {
    const start = this.dateFormatService.parseUserDateTimeToTimestamp(startValue);
    const end = this.dateFormatService.parseUserDateTimeToTimestamp(endValue);
    if (start == null || end == null || end <= start) {
      this.toastService.showWarning('Enter a valid start and end time, with the end after the start.', true);
      return;
    }
    this.analyticsChartComponent?.zoomToRange(start, end, false);
  }

  resetXAxisRange(startInput: HTMLInputElement, endInput: HTMLInputElement): void {
    this.analyticsChartComponent?.resetXRange();
    this.refreshXAxisInputs(startInput, endInput);
  }

  applyYAxisRange(control: YAxisControl, minValue: string, maxValue: string): void {
    const min = Number(minValue);
    const max = Number(maxValue);
    if (minValue.trim() === '' || maxValue.trim() === '' || !Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
      this.toastService.showWarning('Enter a valid range where the maximum is greater than the minimum.', true);
      return;
    }
    this.analyticsChartComponent?.setYAxisRange(control.axisIndex, min, max);
    this.refreshYAxisControls();
  }

  resetYAxisRange(control: YAxisControl, minInput?: HTMLInputElement, maxInput?: HTMLInputElement): void {
    this.analyticsChartComponent?.resetYAxisRange(control.axisIndex);
    this.refreshYAxisControls();
    // [value] is a one-way binding, so when the recomputed auto range equals the number Angular
    // last wrote it won't overwrite a value the user has since typed into the box. Reset the
    // inputs imperatively so Reset always visually reverts them to the recomputed range.
    const updated = this.yAxisControls.find((c) => c.axisIndex === control.axisIndex);
    if (minInput) {
      minInput.value = updated?.min != null ? String(updated.min) : '';
    }
    if (maxInput) {
      maxInput.value = updated?.max != null ? String(updated.max) : '';
    }
  }

  private rebuildAlertOverlays(): void {
    const defaultRangeEnd = this.selectedDateRange?.end
      ? this.dateFormatService.normalizeTimestamp(this.selectedDateRange.end)
      : Date.now();

    const notificationPoints: NotificationPoint[] = [];
    const alertPeriods: AlertPeriodOverlay[] = [];
    const thresholdByValue = new Map<string, ThresholdOverlayLine>();

    this.notificationsList.forEach((notification) => {
      const severity = this.resolveSeverity(notification.severity);
      const state = this.resolveState(notification.state);
      const label = notification.notificationName || this.selectedParameter?.name || 'Alert';

      const sentAt = this.toTimestampMs(notification.notificationSentDate ?? notification.sentDate);
      if (sentAt != null) {
        notificationPoints.push({
          timestamp: sentAt,
          sentAt,
          label,
          severity,
          state,
          channel: notification.deliveryLogs?.[0]?.displayChannelLabel,
        });
      }

      const startedAt = this.toTimestampMs(notification.startedAt) ?? sentAt;
      const endedAt = this.toTimestampMs(notification.endedAt) ?? defaultRangeEnd;
      if (startedAt != null && endedAt >= startedAt) {
        alertPeriods.push({
          id: notification.id,
          notificationName: label,
          severity,
          state,
          startedAt,
          endedAt,
          sentAt: sentAt ?? undefined,
          peakAt: this.toTimestampMs(notification.peakAt) ?? undefined,
          peakValue: typeof notification.peakValue === 'number' ? notification.peakValue : undefined,
          channel: notification.deliveryLogs?.[0]?.displayChannelLabel,
        });
      }

      if (typeof notification.value === 'number' && Number.isFinite(notification.value)) {
        const thresholdKey = `${notification.value}:${severity}`;
        if (!thresholdByValue.has(thresholdKey)) {
          thresholdByValue.set(thresholdKey, {
            value: notification.value,
            severity,
            label: `${alertSeverityLabel(severity)} threshold`,
          });
        }
      }
    });

    const thresholdLinesFromParameter = this.buildThresholdLinesFromParameterThresholds(this.parameterThresholds);

    // amCharts XY series associate bullet sprites/tooltips with x-positions by
    // ascending valueX; the AlertEvents endpoint returns descending date order, so
    // sort overlays ascending here to keep markers/bands at their real positions.
    this.notificationPoints = notificationPoints.sort((left, right) => left.timestamp - right.timestamp);
    this.alertPeriods = alertPeriods.sort((left, right) => left.startedAt - right.startedAt);
    this.thresholdLines = thresholdLinesFromParameter.length
      ? thresholdLinesFromParameter
      : Array.from(thresholdByValue.values()).sort((left, right) => left.value - right.value);
  }

  private getParameterThresholds(parameter: Parameter | null): ParameterThreshold[] {
    const thresholds = parameter?.thresholds;

    if (!Array.isArray(thresholds)) {
      return [];
    }

    return thresholds.filter((threshold) => Number.isFinite(Number(threshold?.minValue)));
  }

  private buildThresholdLinesFromParameterThresholds(thresholds: ParameterThreshold[]): ThresholdOverlayLine[] {
    if (!thresholds?.length) {
      return [];
    }

    const parameterType = this.selectedParameter?.parameterType;
    if (!Number.isFinite(parameterType)) {
      return [];
    }

    return thresholds
      .map((threshold, index) => {
        const baseValue = Number(threshold.minValue);
        if (!Number.isFinite(baseValue)) {
          return null;
        }

        const convertedValue = Number(
          this.valueConversionService.getParameterValueFromUserPreferences(baseValue, parameterType)
        );

        if (!Number.isFinite(convertedValue)) {
          return null;
        }

        const severity = this.mapThresholdSeverity(threshold.label, index, thresholds.length);

        return {
          value: convertedValue,
          severity,
          label: threshold.label || `${alertSeverityLabel(severity)} threshold`,
          colorHex: normalizeThresholdColorHex(threshold.colorHex),
        } as ThresholdOverlayLine;
      })
      .filter((threshold): threshold is ThresholdOverlayLine => threshold != null)
      .sort((left, right) => left.value - right.value);
  }

  private mapThresholdSeverity(name: string | undefined, index: number, total: number): AlertSeverityLevel {
    const normalizedName = (name || '').trim().toLowerCase();

    if (normalizedName.includes('critical')) {
      return AlertSeverityLevel.Critical;
    }
    if (normalizedName.includes('alert') || normalizedName.includes('high')) {
      return AlertSeverityLevel.Alert;
    }
    if (normalizedName.includes('action') || normalizedName.includes('medium') || normalizedName.includes('moderate')) {
      return AlertSeverityLevel.Action;
    }
    if (normalizedName.includes('watch') || normalizedName.includes('low')) {
      return AlertSeverityLevel.Watch;
    }

    if (total <= 1) {
      return AlertSeverityLevel.Watch;
    }

    const position = total > 1 ? index / (total - 1) : 0;
    if (position >= 0.75) {
      return AlertSeverityLevel.Critical;
    }
    if (position >= 0.5) {
      return AlertSeverityLevel.Alert;
    }
    if (position >= 0.25) {
      return AlertSeverityLevel.Action;
    }

    return AlertSeverityLevel.Watch;
  }

  private updateAlertHeaderState(): void {
    const activeSeverities = this.notificationsList
      .filter((notification) => {
        const state = this.resolveState(notification.state);
        return state === AlertEventState.Active || state === AlertEventState.Escalated;
      })
      .map((notification) => this.resolveSeverity(notification.severity));

    this.highestActiveSeverity = activeSeverities.length
      ? Math.max(...activeSeverities)
      : null;

    this.hasActiveAlerts = this.highestActiveSeverity != null;
  }

  private resolveSeverity(severity: AlertSeverityLevel | number | undefined): AlertSeverityLevel | number {
    if (severity === AlertSeverityLevel.Critical || severity === AlertSeverityLevel.Alert || severity === AlertSeverityLevel.Action || severity === AlertSeverityLevel.Watch) {
      return severity;
    }

    return UNKNOWN_ALERT_SEVERITY;
  }

  private resolveState(state: AlertEventState | undefined): AlertEventState {
    if (state === AlertEventState.Resolved || state === AlertEventState.Acknowledged || state === AlertEventState.Escalated || state === AlertEventState.Active) {
      return state;
    }

    return AlertEventState.Active;
  }

  private toTimestampMs(value: Date | string | number | null | undefined): number | null {
    return toTimestampMs(value, this.dateFormatService);
  }

  private resolveSeriesParameterName(paramId: number): string {
    const selectedParameterType = this.selectedParameter?.parameterType;
    const selectedParameterName = this.selectedParameter?.name?.trim();

    if (selectedParameterType === paramId && selectedParameterName) {
      return selectedParameterName;
    }

    const plottedParameterName = this.parameters
      .find((parameter) => parameter.id === paramId)
      ?.value
      ?.trim();

    if (plottedParameterName) {
      return plottedParameterName;
    }

    return this.chartService.getParameterName(paramId) || `Parameter ${paramId}`;
  }

  private getCommentRangeBounds(): DateTimeRange {
    const zoomStart = this.lastZoom?.start;
    const zoomEnd = this.lastZoom?.end;
    if (typeof zoomStart === 'number' && typeof zoomEnd === 'number' && zoomEnd > zoomStart) {
      return { start: zoomStart, end: zoomEnd };
    }

    let minTimestamp = Number.POSITIVE_INFINITY;
    let maxTimestamp = Number.NEGATIVE_INFINITY;
    this.visibleSeriesData.forEach((series) => {
      series.data.forEach((point) => {
        if (Number.isFinite(point.timestamp)) {
          minTimestamp = Math.min(minTimestamp, point.timestamp);
          maxTimestamp = Math.max(maxTimestamp, point.timestamp);
        }
      });
    });

    if (Number.isFinite(minTimestamp) && Number.isFinite(maxTimestamp) && maxTimestamp > minTimestamp) {
      return { start: minTimestamp, end: maxTimestamp };
    }

    const selectedStart = this.selectedDateRange?.start;
    const selectedEnd = this.selectedDateRange?.end;
    if (typeof selectedStart === 'number' && typeof selectedEnd === 'number' && selectedEnd > selectedStart) {
      return {
        start: this.dateFormatService.normalizeTimestamp(selectedStart),
        end: this.dateFormatService.normalizeTimestamp(selectedEnd),
      };
    }

    const end = Date.now();
    return { start: end - (60 * 60 * 1000), end };
  }

  private highlightSection(startTime: number, endTime: number): void {
    this.selectedSectionRange = {
      start: Math.min(startTime, endTime),
      end: Math.max(startTime, endTime),
    };
  }

  selectCommentRange(comment: Comment): void {
    this.selectedCommentId = comment.id;
    this.resetSelectedRange();

    const selectedRange = this.noteRanges.find((range) => range.id === comment.id);
    if (selectedRange) {
      this.analyticsChartComponent?.zoomToRange(selectedRange.start, selectedRange.end);
    }
  }

  private getActiveParameterIds(): number[] {
    const selectedParameterIds = this.parameters
      .filter((parameter) => parameter.selected)
      .map((parameter) => parameter.id);

    return Array.from(new Set([
      this.selectedParameter.parameterType,
      ...selectedParameterIds,
    ]));
  }

  private getActiveParameters(): NodeParameterType[] {
    const activeParameterIds = new Set(this.getActiveParameterIds());
    return this.parameters.filter((parameter) => activeParameterIds.has(parameter.id));
  }

  private getActiveEndpoints(): SelectedEndpoint[] {
    const baseEndpoint = this.endPoints?.find((endpoint) => endpoint.id === this.endPointId);
    const additionalEndpoints = this.selectedEndPoints.filter((endpoint) => endpoint.selected);

    return [
      ...(baseEndpoint ? [{ ...baseEndpoint, selected: true, color: this.getSeriesColor(0) }] : []),
      ...additionalEndpoints,
    ];
  }

  private buildNodeRecordRequests(): Observable<NodeRecordChartModel>[] {
    this.endPointsSeries = this.getActiveEndpoints().map((endpoint) => endpoint.id);
    return this.endPointsSeries.map((endpointId) => this.sensorRecordService.getNodeRecords(this.nodeRecordsPayload(endpointId)));
  }

  private getNodeColorMap(): Map<string, string> {
    return new Map(this.getActiveEndpoints().map((endpoint, index) => [endpoint.id, endpoint.color ?? this.getSeriesColor(index)]));
  }

  private syncCompareNodes(): void {
    this.compareNodes = this.selectedEndPoints.filter((endpoint) => endpoint.selected);
  }
}
